import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';

import ConsentAlert from '@components/Alert/Type/ConsentAlert';
import ContentLoader from '@components/ContentLoader';
import CurationBanner from '@components/CurationBanner';
import DesktopHeader from '@components/DesktopHeader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import Icon from '@components/Icon/Icon';
import MobileBottomMenu from '@components/MobileBottomMenu';
import MobileSideMenu from '@components/MobileSideMenu';
import SideStripDesktop from '@components/SideStripDesktop';

import { useCheckConsent } from '@hooks/useCheckConsent';
import { useCuration } from '@hooks/useCuration';
import { useDrawer } from '@hooks/useDrawer';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSession } from '@hooks/useSession';

import { isClient, isPatient, isUser } from '@utils/role';

import {
  CuratorAppContent,
  Hamburger,
  MobileHeaderBar,
  MobileWrapper,
} from './AppLayout.style';
import { processViewSettings } from './util';

const CURATOR_SETTINGS = [
  [
    'home',
    'adminManagement',
    'clients',
    'alerts',
    'tasks',
    'discern',
    // 'addAccount',
    'appInformation',
  ],
] as const;

const CURATION_NON_PATIENT_VIEW_SETTINGS = [
  ['backToClients'],
  ['clientOverview'],
  ['settings'],
] as const;

const CURATOR_SETTINGS_MOBILE = [
  [
    'home',
    'adminManagement',
    'clients',
    'alerts',
    'tasks',
    'discern',
    // 'addAccount',
  ],
] as const;

const CURATION_NON_PATIENT_VIEW_SETTINGS_MOBILE = [
  ['backToClients', 'clientOverview'],
] as const;

const CuratorLayout: React.FC<{
  friendlyName?: string | null;
  children: React.ReactNode;
  loading?: boolean;
  mobile?: boolean;
  showHeader?: boolean;
}> = ({
  friendlyName,
  children = null,
  loading: baseLoading = false,
  mobile = false,
  showHeader = false,
}) => {
  const [openMobileDrawer, setOpenMobileDrawer] = useState(false);
  const {
    state: { activeProfile },
  } = useSession();
  const {
    state: { patientView },
  } = useCuration();
  const {
    state: { isOpen },
  } = useDrawer();
  const { flags } = useFeatureFlags();
  const {
    data: { isUnconsentingPatient, hasConsented },
    loading: consentLoading,
  } = useCheckConsent();
  const loading = baseLoading || consentLoading;

  const CURATION_PATIENT_VIEW_SETTINGS = processViewSettings([
    [['backToClients', true]],
    [['clientOverview', true]],
    [
      ['home', true],
      ['discern', true],
      ['alerts', true],
      ['records', true],
      ['timeline', true],
      ['reminders', true],
      ['profile', true],
      ['contacts', true],
      ['assessments', flags.isAssessmentListAssessmentsEnabled],
      ['healthDevices', true],
      ['shareHistory', true],
      ['report', true],
      ['more', true],
      ['appInformation', true],
    ],
  ]);

  const CURATION_PATIENT_VIEW_SETTINGS_MOBILE = processViewSettings([
    [
      ['backToClients', true],
      ['clientOverview', true],
      ['alerts', true],
      ['home', true],
      ['discern', true],
      ['alerts', true],
      ['records', true],
      ['timeline', true],
      ['routines', true],
      ['summary', true],
      ['assessments', flags.isAssessmentListAssessmentsEnabled],
      ['healthDevices', true],
      ['shareHistory', true],
    ],
  ]);

  const isCaregiver =
    activeProfile &&
    isClient(activeProfile) &&
    isUser(activeProfile) &&
    !isPatient(activeProfile);

  return mobile ? (
    <>
      {isUnconsentingPatient || !hasConsented ? <ConsentAlert /> : null}
      <MobileSideMenu
        isOpen={openMobileDrawer}
        onClose={() => setOpenMobileDrawer(false)}
      />
      {showHeader ? (
        <MobileHeaderBar>
          <Hamburger onClick={() => setOpenMobileDrawer(true)}>
            <Icon color="text.primary" name="bars" type="fal" />
          </Hamburger>
          {/* TODO
              <NotificationBell
                onClick={() => {
                  navigate(ROUTES.NOTIFICATIONS);
                }}
              /> */}
        </MobileHeaderBar>
      ) : null}
      <MobileWrapper>
        <ContentLoader loading={loading}>{children}</ContentLoader>
      </MobileWrapper>
      <MobileBottomMenu
        settings={
          isCaregiver
            ? CURATION_NON_PATIENT_VIEW_SETTINGS_MOBILE
            : patientView
              ? CURATION_PATIENT_VIEW_SETTINGS_MOBILE
              : CURATOR_SETTINGS_MOBILE
        }
      />
    </>
  ) : (
    <>
      <SideStripDesktop
        settings={
          isCaregiver
            ? CURATION_NON_PATIENT_VIEW_SETTINGS
            : patientView
              ? CURATION_PATIENT_VIEW_SETTINGS
              : CURATOR_SETTINGS
        }
      />
      <DesktopHeader friendlyName={friendlyName} />
      {patientView ? <CurationBanner /> : null}
      <CuratorAppContent curating={patientView} drawerOpen={isOpen}>
        <ContentLoader loading={loading}>
          {isUnconsentingPatient || !hasConsented ? <ConsentAlert /> : null}
          {children}
        </ContentLoader>
      </CuratorAppContent>
    </>
  );
};

export default withErrorBoundary(CuratorLayout, {
  FallbackComponent: ErrorBoundaryFallback,
});
